// src/components/ScrollTopButton.jsx
import React, { useEffect, useState } from "react";

export default function ScrollTopButton({
  fadeInAt = 300,
  scrollToTopMs = 600,
}) {
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(0); // 0..1
  const [parallaxOffset, setParallaxOffset] = useState(0);

  useEffect(() => {
    function onScroll() {
      const sc = window.scrollY || window.pageYOffset;
      setVisible(sc > fadeInAt);
      const docH = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight);
      const winH = window.innerHeight;
      const max = Math.max(1, docH - winH);
      const p = Math.min(1, sc / max);
      setProgress(p);
      
      // Subtle parallax effect (moves slightly opposite to scroll)
      const parallax = (sc * 0.05) % 20; // Small movement, cycles every 20px
      setParallaxOffset(parallax);
    }
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [fadeInAt]);

  function handleClick() {
    const start = performance.now();
    const from = window.scrollY;
    function step(now) {
      const t = Math.min(1, (now - start) / scrollToTopMs);
      const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
      window.scrollTo(0, Math.round(from * (1 - eased)));
      if (t < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  return (
    <>
      <style>{`
        .stb {
          position: fixed;
          right: 22px;
          bottom: 28px;
          width: 56px;
          height: 56px;
          border-radius: 999px;
          display: grid;
          place-items: center;
          cursor: pointer;
          z-index: 200;
          transition: opacity 300ms ease, transform 300ms cubic-bezier(.2,.9,.2,1);
          opacity: 0;
          pointer-events: none;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12), 0 4px 12px rgba(251, 191, 36, 0.08);
          will-change: transform;
        }
        .stb.visible { 
          opacity: 1; 
          pointer-events: auto; 
          transform: translateY(0); 
        }
        .stb.hidden { 
          transform: translateY(8px); 
        }
        
        .stb:hover {
          transform: translateY(-2px) scale(1.05);
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.16), 0 6px 16px rgba(251, 191, 36, 0.12);
        }
        
        .stb:active {
          transform: translateY(0) scale(0.98);
        }

        /* Mobile optimizations */
        @media (max-width: 768px) {
          .stb {
            right: 16px;
            bottom: 20px;
            width: 52px;
            height: 52px;
          }
          
          .stb .icon {
            width: 20px;
            height: 20px;
          }
        }
        
        @media (max-width: 480px) {
          .stb {
            right: 12px;
            bottom: 16px;
            width: 48px;
            height: 48px;
          }
          
          .stb .icon {
            width: 18px;
            height: 18px;
          }
        }

        /* glassmorphic circle background */
        .stb .shell {
          position: relative;
          width: 100%;
          height: 100%;
          border-radius: 999px;
          overflow: visible;
          background: rgba(255, 255, 255, 0.25);
          backdrop-filter: blur(20px) saturate(180%);
          -webkit-backdrop-filter: blur(20px) saturate(180%);
          border: 1px solid rgba(255, 255, 255, 0.3);
          display: flex; 
          align-items: center; 
          justify-content: center;
        }
        
        /* Inner container to clip the liquid */
        .stb .shell-inner {
          position: absolute;
          inset: 0;
          border-radius: 999px;
          overflow: hidden;
        }

        /* the liquid fill layer with wave effect */
        .stb .fill {
          position: absolute;
          left: -5%;
          right: -5%;
          bottom: 0;
          width: 110%;
          height: calc(var(--fill, 0%));
          background: linear-gradient(180deg, 
            rgba(251, 191, 36, 0.5), 
            rgba(245, 158, 11, 0.6),
            rgba(251, 146, 60, 0.65)
          );
          transition: height 200ms cubic-bezier(.2,.9,.2,1);
          z-index: 1;
        }

        /* Wave container */
        .stb .wave-container {
          position: absolute;
          top: -10px;
          left: -5%;
          width: 110%;
          height: 20px;
          overflow: visible;
        }

        /* First wave (primary) - SVG-like curve */
        .stb .fill::before {
          content: "";
          position: absolute;
          top: -12px;
          left: -50%;
          width: 200%;
          height: 30px;
          background: 
            radial-gradient(ellipse 50px 18px at 20% 40%, rgba(245, 158, 11, 0.8) 0%, transparent 50%),
            radial-gradient(ellipse 45px 16px at 50% 45%, rgba(251, 191, 36, 0.7) 0%, transparent 50%),
            radial-gradient(ellipse 50px 18px at 80% 40%, rgba(245, 158, 11, 0.8) 0%, transparent 50%);
          animation: wave-primary 3s ease-in-out infinite;
          filter: blur(1px);
        }

        /* Second wave (secondary) - offset curve */
        .stb .fill::after {
          content: "";
          position: absolute;
          top: -10px;
          left: -50%;
          width: 200%;
          height: 26px;
          background: 
            radial-gradient(ellipse 45px 15px at 30% 45%, rgba(251, 146, 60, 0.6) 0%, transparent 50%),
            radial-gradient(ellipse 40px 13px at 65% 50%, rgba(245, 158, 11, 0.5) 0%, transparent 50%),
            radial-gradient(ellipse 45px 15px at 95% 45%, rgba(251, 146, 60, 0.6) 0%, transparent 50%);
          animation: wave-secondary 2.5s ease-in-out infinite;
          animation-delay: -1.25s;
          filter: blur(1px);
        }

        @keyframes wave-primary {
          0% { 
            transform: translateX(0) translateZ(0);
          }
          100% { 
            transform: translateX(50%) translateZ(0);
          }
        }

        @keyframes wave-secondary {
          0% { 
            transform: translateX(0) translateZ(0);
          }
          100% { 
            transform: translateX(50%) translateZ(0);
          }
        }

        /* Add shimmer effect to liquid */
        .stb .fill-shimmer {
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.3),
            transparent
          );
          animation: shimmer 3s ease-in-out infinite;
          z-index: 2;
        }

        @keyframes shimmer {
          0%, 100% {
            left: -100%;
          }
          50% {
            left: 200%;
          }
        }

        /* icon above fill */
        .stb .icon {
          position: relative;
          z-index: 3;
          width: 22px; 
          height: 22px;
          display: inline-block;
          filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
        }

        /* Dynamic icon color based on fill */
        .stb[data-filled="true"] .icon path {
          stroke: #ffffff;
          transition: stroke 300ms ease;
        }

        .stb[data-filled="false"] .icon path {
          stroke: #0b1220;
          transition: stroke 300ms ease;
        }

        /* tooltip when hovered */
        .stb .hint {
          position: absolute;
          bottom: 66px;
          right: 0;
          transform: translateY(6px);
          background: rgba(25, 25, 25, 0.94);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          color: #fff;
          padding: 8px 12px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          opacity: 0;
          pointer-events: none;
          transition: opacity 180ms ease, transform 180ms ease;
          white-space: nowrap;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        }
        
        .stb .hint::after {
          content: "";
          position: absolute;
          bottom: -4px;
          right: 18px;
          width: 8px;
          height: 8px;
          background: rgba(25, 25, 25, 0.94);
          transform: rotate(45deg);
        }
        
        .stb:hover .hint { 
          opacity: 1; 
          transform: translateY(0); 
        }

        /* when not visible keep pointer-events blocked */
        .stb[aria-hidden="true"] { 
          pointer-events: none; 
        }

        /* Bubble particles inside liquid */
        .stb .bubble {
          position: absolute;
          bottom: 0;
          background: rgba(255, 255, 255, 0.4);
          border-radius: 50%;
          animation: rise linear infinite;
          z-index: 2;
        }

        @keyframes rise {
          0% {
            bottom: 0%;
            opacity: 0;
            transform: translateX(0) scale(1);
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 0.8;
          }
          100% {
            bottom: 100%;
            opacity: 0;
            transform: translateX(20px) scale(0.5);
          }
        }

        /* Overflow drops when button is full */
        .stb .overflow-drop {
          position: absolute;
          width: 5px;
          height: 7px;
          background: linear-gradient(180deg, 
            rgba(251, 191, 36, 0.85), 
            rgba(245, 158, 11, 0.9)
          );
          border-radius: 50% 50% 50% 50% / 60% 60% 40% 40%;
          opacity: 0;
          filter: blur(0.5px);
          z-index: 5;
          pointer-events: none;
        }

        .stb[data-overflowing="true"] .overflow-drop {
          animation: drip 2.5s ease-in infinite;
        }

        @keyframes drip {
          0% {
            opacity: 0;
            transform: translateY(-3px) scale(0.8);
          }
          8% {
            opacity: 1;
            transform: translateY(-2px) scale(1);
          }
          12% {
            transform: translateY(-1px) scale(0.95);
          }
          18% {
            transform: translateY(0px) scale(0.9);
          }
          25% {
            opacity: 1;
            transform: translateY(4px) scale(0.85);
          }
          40% {
            opacity: 0.8;
            transform: translateY(15px) scale(0.7);
          }
          60% {
            opacity: 0.5;
            transform: translateY(30px) scale(0.5);
          }
          100% {
            opacity: 0;
            transform: translateY(50px) scale(0.2);
          }
        }

        /* Subtle brim effect at top of button when full */
        .stb .brim {
          position: absolute;
          top: -3px;
          left: 50%;
          transform: translateX(-50%);
          width: 88%;
          height: 5px;
          background: linear-gradient(90deg,
            transparent,
            rgba(251, 191, 36, 0.5) 20%,
            rgba(245, 158, 11, 0.6) 50%,
            rgba(251, 191, 36, 0.5) 80%,
            transparent
          );
          border-radius: 50%;
          opacity: 0;
          z-index: 4;
          filter: blur(1.5px);
          pointer-events: none;
        }

        .stb[data-overflowing="true"] .brim {
          animation: brim-pulse 2.5s ease-in-out infinite;
        }

        @keyframes brim-pulse {
          0%, 100% {
            opacity: 0;
            transform: translateX(-50%) scaleX(0.85) scaleY(0.8);
          }
          50% {
            opacity: 0.9;
            transform: translateX(-50%) scaleX(1.05) scaleY(1.1);
          }
        }

      `}</style>

      <button
        type="button"
        className={`stb ${visible ? "visible" : "hidden"}`}
        aria-hidden={!visible}
        onClick={handleClick}
        title="Back to top"
        data-filled={progress > 0.5}
        data-overflowing={progress >= 0.98}
        style={{
          ["--fill"]: `${Math.max(6, Math.round(progress * 100))}%`,
          transform: visible ? `translateY(${parallaxOffset * -0.3}px)` : 'translateY(8px)',
        }}
      >
        <div className="shell" aria-hidden>
          {/* Brim effect when full */}
          {progress >= 0.98 && <div className="brim" />}
          
          {/* Overflow drops when full - positioned outside button */}
          {progress >= 0.98 && (
            <>
              <span 
                className="overflow-drop" 
                style={{ 
                  top: '-4px',
                  left: '30%',
                  animationDelay: '0s'
                }} 
              />
              <span 
                className="overflow-drop" 
                style={{ 
                  top: '-5px',
                  left: '50%',
                  animationDelay: '0.8s'
                }} 
              />
              <span 
                className="overflow-drop" 
                style={{ 
                  top: '-3px',
                  left: '68%',
                  animationDelay: '1.6s'
                }} 
              />
            </>
          )}
          
          {/* Inner clipping container for liquid */}
          <div className="shell-inner">
            <div
              className="fill"
              style={{
                height: `${Math.max(6, Math.round(progress * 100))}%`,
              }}
            >
              {/* Shimmer effect */}
              <div className="fill-shimmer" />
              
              {/* Animated bubbles */}
              {progress > 0.1 && (
                <>
                  <span 
                    className="bubble" 
                    style={{ 
                      left: '30%', 
                      width: '4px', 
                      height: '4px',
                      animationDuration: '2.5s',
                      animationDelay: '0s'
                    }} 
                  />
                  <span 
                    className="bubble" 
                    style={{ 
                      left: '60%', 
                      width: '3px', 
                      height: '3px',
                      animationDuration: '3s',
                      animationDelay: '0.8s'
                    }} 
                  />
                  <span 
                    className="bubble" 
                    style={{ 
                      left: '75%', 
                      width: '5px', 
                      height: '5px',
                      animationDuration: '2.8s',
                      animationDelay: '1.5s'
                    }} 
                  />
                </>
              )}
            </div>
          </div>
          
          <svg className="icon" viewBox="0 0 24 24" aria-hidden>
            <path 
              fill="none" 
              stroke="#0b1220" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
              d="M12 16V8M8 12l4-4 4 4" 
            />
          </svg>

          {/* tooltip shows when hovered */}
          <div className="hint" aria-hidden={!visible}>
            Back to top
          </div>
        </div>
      </button>
    </>
  );
}